import React, { useEffect, useRef, useState } from "react";
import { SoundToggle } from "@/components/SoundToggle";
import { Eye, Check, X, Trophy, RotateCcw, Timer } from "lucide-react";
import { RestGlyph, StaveLines } from "@/components/NotationDiagrams";
import { WeeklyBoard } from "@/components/WeeklyBoard";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { reportScore } from "@/lib/gameScores";
import { playRight, playWrong } from "@/lib/feedbackSounds";

const RESTS = [[4, "Semibreve rest", "4 beats"], [3, "Dotted minim rest", "3 beats"], [2, "Minim rest", "2 beats"], [1, "Crotchet rest", "1 beat"], [0.5, "Quaver rest", "½ beat"]];
const SECONDS = 60;
const KEY = "jt-rest-spotter-best-60";
const label = (r) => `${r[1]} · ${r[2]}`;
const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);
const makeQ = (prev) => {
  let r; do { r = RESTS[Math.floor(Math.random() * RESTS.length)]; } while (r === prev);
  return { r, options: shuffle([r, ...shuffle(RESTS.filter((x) => x !== r)).slice(0, 3)]) };
};

// 60-second rest spotting: name as many rests as you can before the clock runs out.
export const RestSpotter = ({ testId = "rest-spotter" }) => {
  const [q, setQ] = useState(null);
  const [left, setLeft] = useState(SECONDS);
  const [score, setScore] = useState(0);
  const [tries, setTries] = useState(0);
  const [picked, setPicked] = useState(null);
  const [over, setOver] = useState(false);
  const [best, setBest] = useState(() => Number(localStorage.getItem(KEY) || 0));
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const scoreRef = useRef(0);
  const nextT = useRef(null);
  useEffect(() => { api.get("/rest-spotter/leaderboard").then(({ data }) => setBoard(data)).catch(() => {}); }, []);
  useEffect(() => () => clearTimeout(nextT.current), []);

  useEffect(() => {
    if (!q || over) return undefined;
    if (left <= 0) {
      clearTimeout(nextT.current);
      setOver(true);
      const s = scoreRef.current;
      if (s > best) { setBest(s); localStorage.setItem(KEY, String(s)); }
      if (user) {
        api.post("/rest-spotter/score", { score: s }).then(({ data }) => setBoard(data)).catch(() => {});
        reportScore("rest-spotter", "Rest spotting", "round", s);
      }
      return undefined;
    }
    const t = setTimeout(() => setLeft((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [q, left, over, best, user]);

  const start = () => { scoreRef.current = 0; setScore(0); setTries(0); setLeft(SECONDS); setOver(false); setPicked(null); setQ(makeQ()); };
  const choose = (o) => {
    if (picked || over) return;
    setPicked(o);
    setTries((t) => t + 1);
    const ok = o === q.r;
    if (ok) { scoreRef.current += 1; setScore(scoreRef.current); playRight(); } else playWrong();
    nextT.current = setTimeout(() => { setPicked(null); setQ((cur) => makeQ(cur.r)); }, ok ? 650 : 1400);
  };
  const ok = picked && picked === q.r;

  return (
    <div className="mt-4 rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary"><Eye size={14} /> Rest spotting</p>
        <p className="flex items-center gap-3 text-xs text-muted-foreground">
          {q && <span data-testid={`${testId}-progress`}>Score <b className="text-foreground">{score}</b> / {tries}</span>}
          <SoundToggle testId={`${testId}-sound-toggle`} />
          <span className="flex items-center gap-1" data-testid={`${testId}-best`}><Trophy size={12} className="text-primary" /> Best: {best}</span>
        </p>
      </div>
      {!q ? (
        <div className="mt-3">
          <p className="text-sm">You have {SECONDS} seconds. A rest flashes up: tap its name and how long it lasts. How many can you get right?</p>
          <button type="button" onClick={start} data-testid={`${testId}-start`} className="mt-3 rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground">Start</button>
        </div>
      ) : (
        <>
          <div className="mt-3 flex items-center gap-3" data-testid={`${testId}-timer`}>
            <Timer size={16} className={left <= 10 && !over ? "text-red-500" : "text-primary"} />
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-border">
              <div className={`h-full transition-[width] duration-1000 ease-linear ${left <= 10 ? "bg-red-500" : "bg-primary"}`} style={{ width: `${(left / SECONDS) * 100}%` }} />
            </div>
            <span className={`w-12 text-right font-mono text-sm font-bold ${left <= 10 ? "text-red-500" : ""}`} data-testid={`${testId}-seconds`}>{left}s</span>
          </div>
          {over ? (
            <div className="mt-4 text-center" data-testid={`${testId}-over`}>
              <p className="text-lg font-semibold">Time's up! You spotted <span className="text-primary">{score}</span> rest{score === 1 ? "" : "s"} correctly{tries ? ` out of ${tries}` : ""}.</p>
              <button type="button" onClick={start} data-testid={`${testId}-again`} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground"><RotateCcw size={13} /> Play again</button>
            </div>
          ) : (
            <>
              <div className={`mx-auto mt-3 max-w-xs rounded-xl border-2 bg-background/60 p-2 transition-colors ${picked ? (ok ? "border-emerald-500" : "border-red-500") : "border-border"}`}>
                <svg key={`${tries}`} viewBox="0 45 160 130" className="w-full animate-in fade-in zoom-in-50 text-foreground duration-300" role="img" aria-label="Which rest is this?" data-testid={`${testId}-glyph`}>
                  <StaveLines x1={0} x2={160} /><RestGlyph x={80} d={q.r[0]} />
                </svg>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {q.options.map((o, k) => {
                  const st = picked && (o === q.r ? "right" : o === picked ? "wrong" : "");
                  return (
                    <button key={o[1]} type="button" onClick={() => choose(o)} data-testid={`${testId}-option-${k}`}
                      className={`flex items-center justify-between rounded-xl border px-3 py-2 text-left text-sm transition-colors ${st === "right" ? "border-emerald-500 bg-emerald-500/15" : st === "wrong" ? "border-red-500 bg-red-500/20 text-red-500" : "border-border hover:border-primary/60"}`}>
                      {label(o)}{st === "right" && <Check size={15} className="text-emerald-500" />}{st === "wrong" && <X size={15} className="text-red-500" />}
                    </button>
                  );
                })}
              </div>
              {picked && (
                <p className={`mt-3 inline-block rounded-full px-4 py-1.5 text-sm font-bold ${ok ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`} data-testid={`${testId}-feedback`}>
                  {ok ? "Correct!" : `Not quite: that's a ${label(q.r).toLowerCase()}.`}
                </p>
              )}
            </>
          )}
        </>
      )}
      <WeeklyBoard title="This week's Rest Spotting board (60 seconds)" board={board} testId={testId} user={user} field="score" unit={(v) => `${v} right`} empty="Finish a 60-second round to get on the board." />
    </div>
  );
};
